/* «Создать кавер» по опубликованной форме (APP-SPEC-5 §9; 03-api §6.6 `cover`, §9.2 «Создать кавер»).
   Ключи заказа: `song_upload_id` (или поле шаблона, которое его покрывает) или `source_order_id` («Мои треки»),
   `duration` (`frag` · `full`), `from_ms`, `style_id` или `own_style`, `style_text`, `voice_mode` (`src` · `voice`),
   `voice_id`. Данные экрана — политика `constraints.cover`: стили, темы, чипы, длительности с ценами, пределы песни.
   Контрол стоит, только когда его ключ опубликован (fields или screen_fields, docs/59). */
import type { StudioForm } from '../studio/studio-api';
import { mediaUrl, record, rows, text } from '../studio/studio-content';
import { coveredBy, readText, screenFields, screenUpload, specKey as keyInfo, type KeyInfo } from '../reels/form-keys';

/** Значение ленты для «Своего стиля» (в заказ уходит own_style, а не style_id) */
export const OWN_STYLE = '__own__';
export interface CoverStyle {
  id: string;
  name: string;
  description: string;
  themes: string[];
  popularity: number;
  hit: boolean;
  isNew: boolean;
  color: string | null;
  previewUrl: string | null;
}
export interface CoverDuration {
  id: 'frag' | 'full';
  title: string;
  price: number | null;
  windowMs: number;
}
export interface CoverPolicy {
  styles: CoverStyle[];
  themes: string[];
  addChips: string[];
  ownChips: string[];
  durations: CoverDuration[];
  minMs: number;
  maxMs: number;
  styleMin: number;
  styleMax: number;
  ownStyle: boolean;
}
const num = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback);
const strings = (value: unknown) => (Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string' && !!x) : []);
const color = (value: unknown) => (typeof value === 'string' && /^#[0-9a-f]{3,8}$/i.test(value) ? value : null);

export function coverPolicy(form: StudioForm): CoverPolicy {
  const p = record(record(form.constraints).cover);
  const chips = record(p.chips);
  const styleField = keyInfo(form, 'style_id');
  const styles = rows(p.styles).flatMap(s => {
    const id = typeof s.id === 'number' ? String(s.id) : text(s.id);
    if (!id || (styleField?.values.length && !styleField.values.includes(id))) return [];
    return [
      {
        id,
        name: text(s.name) || id,
        description: text(s.description) || text(s.sub),
        themes: strings(s.themes),
        popularity: Number(s.popularity) || 0,
        hit: s.hit === true || s.badge === 'hit',
        isNew: s.new === true || s.badge === 'new',
        color: color(s.color) ?? color(record(s.tint).from),
        previewUrl: mediaUrl(s.preview_url),
      },
    ];
  });
  // Стили без политики: только значения поля (имя — id), чтобы выбор не был выдуманным.
  const fallback = !styles.length && styleField ? styleField.values.map(id => ({ id, name: id, description: '', themes: [], popularity: 0, hit: false, isNew: false, color: null, previewUrl: null })) : [];
  const durationField = keyInfo(form, 'duration');
  const durations = rows(p.durations).flatMap(d => {
    const id = text(d.id);
    if ((id !== 'frag' && id !== 'full') || (durationField?.values.length && !durationField.values.includes(id))) return [];
    return [{ id: id as CoverDuration['id'], title: text(d.title) || (id === 'full' ? 'Вся песня' : 'Фрагмент 1:00'), price: typeof d.price === 'number' ? d.price : null, windowMs: num(d.window_ms, 60000) }];
  });
  const durationFallback: CoverDuration[] =
    !durations.length && durationField
      ? durationField.values.flatMap(id => (id === 'frag' || id === 'full' ? [{ id, title: id === 'full' ? 'Вся песня' : 'Фрагмент 1:00', price: null, windowMs: 60000 }] : []))
      : [];
  const styleText = keyInfo(form, 'style_text');
  return {
    styles: [...styles, ...fallback],
    themes: strings(p.themes),
    addChips: strings(chips.add ?? p.add_chips),
    ownChips: strings(chips.own ?? p.own_chips),
    durations: [...durations, ...durationFallback],
    minMs: num(p.min_duration_ms, 6000),
    maxMs: num(p.max_duration_ms ?? songField(form)?.upload?.max_duration_ms, 600000),
    styleMin: num(p.style_text_min, 10),
    styleMax: Math.min(300, num(p.style_text_max ?? styleText?.maxLength, 300)),
    ownStyle: !!keyInfo(form, 'own_style'),
  };
}

/** Поле загрузки песни: `song_upload_id` или поле шаблона, которое его покрывает (`screen_contract.covered[]`). */
export function songField(form: StudioForm) {
  const by = coveredBy(form, 'song_upload_id');
  return (
    form.fields.find(f => f.key === 'song_upload_id' && f.upload?.kind === 'audio') ??
    form.fields.find(f => by.includes(f.key) && f.upload?.kind === 'audio') ??
    form.fields.find(f => f.upload?.kind === 'audio')
  );
}
/** Песня: поле шаблона (хук формы грузит его сам) или JSON-ключ контракта `song_upload_id` с описанием загрузки
    (экран грузит файл сам, use-screen-upload). */
export interface CoverSong {
  key: string;
  screen: boolean;
  accept: string[];
  maxBytes: number;
}
export function songSlot(form: StudioForm): CoverSong | null {
  const own = songField(form);
  if (own) return { key: own.key, screen: false, accept: own.upload?.accept ?? [], maxBytes: own.upload?.max_bytes ?? 100 * 1024 * 1024 };
  const upload = screenUpload(form, 'song_upload_id');
  return upload?.kind === 'audio' ? { key: 'song_upload_id', screen: true, accept: upload.accept, maxBytes: 100 * 1024 * 1024 } : null;
}
export function songValue(form: StudioForm, inputs: Record<string, string>): string {
  const slot = songSlot(form);
  if (!slot) return '';
  return slot.screen ? readText(keyInfo(form, slot.key), inputs) : (inputs[slot.key] ?? '');
}
const coverKeys = new Set(['song_upload_id', 'source_order_id', 'duration', 'from_ms', 'style_id', 'own_style', 'style_text', 'voice_mode', 'voice_id', 'rights_confirmed']);
/** Форма, которую экран умеет показать: песня и стиль опубликованы (своим ключом или покрывающим полем шаблона),
    незнакомых обязательных ключей нет. */
export function coverSupported(form: StudioForm): boolean {
  if (form.screen !== 'cover' || !songSlot(form) || !(keyInfo(form, 'style_id') || keyInfo(form, 'own_style'))) return false;
  const covering = new Set([...coverKeys].flatMap(key => coveredBy(form, key)));
  const unknown = (f: { key: string }) =>
    !coverKeys.has(f.key) && !covering.has(f.key) && !/^consent_(accepted|version)$/.test(f.key) && f.key !== songSlot(form)?.key;
  return form.fields.every(f => !unknown(f) || !f.required) && screenFields(form).every(f => !unknown(f) || !f.required);
}

export const mmss = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

export interface CoverChoice {
  songMs: number | null;
  own: boolean;
}
/** Чего не хватает (APP-SPEC-5 §9 «Кнопка и цена»): песня, прочитанная длительность, описание своего стиля. */
export function coverMissing(form: StudioForm, policy: CoverPolicy, inputs: Record<string, string>, rights: boolean, choice: CoverChoice): string | null {
  if (!coverSupported(form)) return 'Создание кавера пока недоступно';
  const songId = songValue(form, inputs);
  const source = readText(keyInfo(form, 'source_order_id'), inputs);
  if (!source) {
    if (!rights) return 'Подтвердите права на песню';
    if (!songId) return 'Загрузите песню';
    if (!/^[0-9a-f-]{36}$/i.test(songId)) return 'Проверьте загрузку песни';
    if (choice.songMs === null) return 'Читаем длительность песни';
    if (choice.songMs < policy.minMs) return 'Песня короче 6 секунд';
    if (choice.songMs > policy.maxMs) return `Песня длиннее ${Math.round(policy.maxMs / 60000)} минут`;
  }
  const styleText = readText(keyInfo(form, 'style_text'), inputs).trim();
  if (choice.own) {
    if (Array.from(styleText).length < policy.styleMin) return `Опишите стиль, от ${policy.styleMin} знаков`;
  } else if (!readText(keyInfo(form, 'style_id'), inputs)) return 'Выберите стиль';
  if (Array.from(styleText).length > policy.styleMax) return `Описание длиннее ${policy.styleMax} знаков`;
  if (readText(keyInfo(form, 'voice_mode'), inputs) === 'voice' && !readText(keyInfo(form, 'voice_id'), inputs)) return 'Выберите голос с переозвучкой';
  return null;
}
export type { KeyInfo };
